import { AT } from "@web-monorepo/translation-runtime";
import { useHashRoute } from "@web-monorepo/routing";
import loadingMojoGif from "../../assets/images/components/loading_mojo/loading-mojo.gif";
import styles from "./loading-mojo.module.css";
import { useLogRenderedDuration } from "./useLogRenderedDuration";

export interface LoadingMojoProps {
  inline?: boolean;
  block?: boolean;
  border?: boolean;
  noOverlay?: boolean;
  /**
   * The name will get used for automated product events.
   * @see https://www.notion.so/classdojo/Automatic-Product-Events-for-Web-bfc580f10a914c3ba514e5ec20f8ef9e?pvs=4
   */
  "data-name"?: string;
  text?: string;
  className?: string;
}

// eslint-disable-next-line react-refresh/only-export-components -- pre-existing violation
export const getApplicationName = () => {
  if (typeof window === "undefined") return "unknown";

  const hostname = window.location.hostname;
  const parts = hostname.split(".");
  return parts[0] || "unknown";
};

export function LoadingMojo({
  inline,
  block,
  border,
  "data-name": dataName = "loadingMojo",
  text,
  noOverlay,
  className,
}: LoadingMojoProps) {
  const hashRoute = useHashRoute();

  useLogRenderedDuration({
    eventName: "loading_mojo_duration",
    metadata: {
      application: getApplicationName(),
      hashRoute,
    },
  });
  if (inline && text) {
    const outerDivClasses = [styles.inline];
    if (className) outerDivClasses.push(className);
    if (border) outerDivClasses.push(styles.border);

    return (
      <div className={outerDivClasses.join(" ")}>
        <div className={styles.inlineTextLayout}>
          <div className={styles.inlineImageWrapper} role="status" aria-live="assertive">
            <img alt="Content is loading" src={loadingMojoGif} data-name={dataName} />
            <div className={styles.visuallyHidden}>
              <AT>Content is loading</AT>
            </div>
          </div>
          <div className={styles.inlineTextContent}>
            <span>{text}</span>
          </div>
        </div>
      </div>
    );
  }

  const rootDivDynamicClassesArr = [];
  if (className) rootDivDynamicClassesArr.push(className);

  const imgDynamicClassesArr = [];

  if (inline) {
    rootDivDynamicClassesArr.push(styles.inline);
    imgDynamicClassesArr.push(styles.inline);
  } else if (block) {
    imgDynamicClassesArr.push(styles.block);
  } else {
    imgDynamicClassesArr.push(styles.full);
  }

  if (border) {
    imgDynamicClassesArr.push(styles.border);
  }

  const rootDivDynamicClasses = rootDivDynamicClassesArr.join(" ");
  const imgDynamicClasses = imgDynamicClassesArr.join(" ");

  return (
    <div className={rootDivDynamicClasses} role={inline ? "alert" : "status"} aria-live="assertive">
      {!inline && !block && !noOverlay && <div className={styles.greyout} />}
      <div className={styles.visuallyHidden}>
        <AT>Content is loading</AT>
      </div>
      <img alt="Content is loading" className={imgDynamicClasses} src={loadingMojoGif} data-name={dataName} />
    </div>
  );
}
